import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, MessageCircle, ThumbsUp, User, TrendingUp, BarChart3, Filter } from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { SkipLink } from '@/components/ui/SkipLink';
import { reviewService } from '@/services/reviewService';
import type { Review } from '@/types';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { CircularProgress } from '@/components/ui/ProgressIndicator';
import { FadeIn } from '@/components/ui/AnimatedComponents';

export function ReviewsPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [filterRating, setFilterRating] = useState<number | null>(null);
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'highest' | 'lowest'>('newest');
  const [showResponseForm, setShowResponseForm] = useState<string | null>(null);
  const [responseText, setResponseText] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  const isProvider = user?.role === 'provider';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!isLoading && !isProvider) {
      navigate('/dashboard');
    }
  }, [isLoading, isProvider, navigate]);

  useEffect(() => {
    loadReviews();
  }, [user?.id]);

  const loadReviews = async () => {
    if (!user?.id) return;

    setIsLoading(true);
    try {
      const providerReviews = reviewService.getProviderReviews(user.id);
      const providerStats = reviewService.getProviderStats(user.id);
      setReviews(providerReviews);
      setStats(providerStats);
    } catch (error) {
      // Failed to load reviews
    } finally {
      setIsLoading(false);
    }
  };

  const handleRespondToReview = async (reviewId: string) => {
    if (!responseText.trim()) return;

    try {
      reviewService.respondToReview(reviewId, responseText);
      setResponseText('');
      setShowResponseForm(null);
      loadReviews();
    } catch (error) {
      // Failed to respond to review
    }
  };

  const getFilteredAndSortedReviews = () => {
    let filtered = reviews;
    if (filterRating) {
      filtered = filtered.filter(review => review.rating === filterRating);
    }
    return [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'newest': return b.createdAt.getTime() - a.createdAt.getTime();
        case 'oldest': return a.createdAt.getTime() - b.createdAt.getTime();
        case 'highest': return b.rating - a.rating;
        case 'lowest': return a.rating - b.rating;
        default: return b.createdAt.getTime() - a.createdAt.getTime();
      }
    });
  };

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star key={star} className={`w-4 h-4 ${star <= rating ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`} />
        ))}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 pb-20">
      <SkipLink href="#main-content">Skip to main content</SkipLink>
      
      <main id="main-content" role="main" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <Breadcrumb items={[{ label: 'Dashboard', href: '/dashboard' }, { label: 'Reviews' }]} />

        <div className="page-header mt-4">
          <h1 className="page-title">Reviews & Reputation</h1>
          <p className="page-description">Monitor your feedback and respond to customers.</p>
        </div>

        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <FadeIn delay={0}>
              <Card className="p-6 hover:shadow-md transition-shadow duration-200">
                <div className="text-sm font-medium text-gray-500 mb-1">Average Rating</div>
                <div className="text-3xl font-bold text-amber-500 flex items-center gap-2">
                  {stats.averageRating.toFixed(1)} <Star className="w-6 h-6 fill-amber-500" />
                </div>
              </Card>
            </FadeIn>
            <FadeIn delay={50}>
              <Card className="p-6 hover:shadow-md transition-shadow duration-200">
                <div className="text-sm font-medium text-gray-500 mb-1">Total Reviews</div>
                <div className="text-3xl font-bold text-gray-900">{stats.totalReviews}</div>
              </Card>
            </FadeIn>
            <FadeIn delay={100}>
              <Card className="p-6 hover:shadow-md transition-shadow duration-200">
                <div className="text-sm font-medium text-gray-500 mb-1">5-Star Reviews</div>
                <div className="text-3xl font-bold text-emerald-600">{stats.ratingDistribution[5] || 0}</div>
              </Card>
            </FadeIn>
            <FadeIn delay={150}>
              <Card className="p-6 hover:shadow-md transition-shadow duration-200">
                <div className="text-sm font-medium text-gray-500 mb-3">Response Rate</div>
                <div className="flex items-center justify-center">
                  <CircularProgress
                    progress={reviews.length > 0 ? Math.round((reviews.filter(r => r.response).length / reviews.length) * 100) : 0}
                    size={100}
                    variant="primary"
                  />
                </div>
              </Card>
            </FadeIn>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Filters */}
          <div className="lg:col-span-1 space-y-6">
            <Card>
              <div className="p-4 border-b border-gray-100 font-semibold text-gray-900 flex items-center gap-2">
                <Filter className="w-4 h-4" /> Filter Reviews
              </div>
              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Rating</label>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setFilterRating(null)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${!filterRating ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}
                    >
                      All
                    </button>
                    {[5, 4, 3, 2, 1].map(rating => (
                      <button
                        key={rating}
                        onClick={() => setFilterRating(rating)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${filterRating === rating ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}
                      >
                        {rating} ★
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as any)}
                    className="w-full form-select rounded-md border-gray-300 text-sm focus:border-primary-500 focus:ring-primary-500 min-h-touch"
                    aria-label="Sort reviews by"
                  >
                    <option value="newest">Newest First</option>
                    <option value="oldest">Oldest First</option>
                    <option value="highest">Highest Rated</option>
                    <option value="lowest">Lowest Rated</option>
                  </select>
                </div>
              </div>
            </Card>
          </div>

          {/* Reviews List */}
          <div className="lg:col-span-2 space-y-4">
            {getFilteredAndSortedReviews().map((review, index) => (
              <FadeIn key={review.id} delay={index * 40}>
                <Card className="p-6 hover:shadow-md transition-shadow duration-200">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-500">
                        <User className="w-5 h-5" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">{review.customerName}</h4>
                        <div className="text-xs text-gray-500">{new Date(review.createdAt).toLocaleDateString()}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      {renderStars(review.rating)}
                    </div>
                  </div>

                  <h5 className="text-sm font-semibold text-gray-900 mb-2">{review.title}</h5>
                  <p className="text-gray-600 text-sm leading-relaxed mb-4">{review.comment}</p>

                  <div className="flex items-center gap-4 mb-4">
                    <button className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700">
                      <ThumbsUp className="w-4 h-4" /> Helpful ({review.helpful})
                    </button>
                  </div>

                  {review.response ? (
                    <div className="bg-gray-50 rounded-lg p-4 text-sm">
                      <div className="font-medium text-gray-900 mb-1 flex items-center gap-2">
                        <MessageCircle className="w-4 h-4 text-primary-500" /> Your Response
                      </div>
                      <p className="text-gray-600">{review.response.message}</p>
                      <div className="text-xs text-gray-400 mt-2">{new Date(review.response.timestamp).toLocaleDateString()}</div>
                    </div>
                  ) : (
                    <div>
                      {showResponseForm === review.id ? (
                        <div className="bg-gray-50 rounded-lg p-4">
                          <textarea
                            value={responseText}
                            onChange={(e) => setResponseText(e.target.value)}
                            placeholder="Write your response..."
                            className="w-full form-textarea rounded-md border-gray-300 text-sm mb-3 focus:border-primary-500 focus:ring-primary-500"
                            rows={3}
                          />
                          <div className="flex gap-2">
                            <Button size="sm" onClick={() => handleRespondToReview(review.id)} disabled={!responseText.trim()}>Post Response</Button>
                            <Button size="sm" variant="ghost" onClick={() => setShowResponseForm(null)}>Cancel</Button>
                          </div>
                        </div>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => setShowResponseForm(review.id)}>Reply to Review</Button>
                      )}
                    </div>
                  )}
                </Card>
              </FadeIn>
            ))}

            {getFilteredAndSortedReviews().length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-500">No reviews found matching your filters.</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}